import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { toast } from 'react-toastify';

import Header from '../../../components/Header';
import config from '../../../config';
import { tokens } from '../../../theme';
import { getAuthToken } from '../../../utils/authStorage';
import { backButtonSx } from '../hpcAdminStatusStyles';

const authHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const AssessmentMemberPicker = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);

  const fetchMembers = useCallback(async (search) => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/members/active`, {
        params: search ? { q: search } : {},
        headers: authHeaders(),
      });
      setMembers(response.data.members || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchMembers(query), 300);
    return () => clearTimeout(timer);
  }, [query, fetchMembers]);

  const handleSelect = async (membershipId) => {
    setCreating(membershipId);
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/hpc/admin/assessments`,
        { membership_id: membershipId },
        { headers: authHeaders() }
      );
      const assessment = response.data.assessment;
      toast.success('Assessment draft created');
      navigate(`/hpc/assessments/${assessment.id}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create assessment');
    } finally {
      setCreating(null);
    }
  };

  return (
    <Box m="20px">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/hpc/assessments')} sx={backButtonSx}>
        Back to assessments
      </Button>

      <Header title="New Assessment" subtitle="Select an active HPC member" />

      <Box mt={2} maxWidth={640}>
        <TextField
          fullWidth
          label="Search by name, membership ID, or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <List
            sx={{
              bgcolor: colors.primary[400],
              borderRadius: 2,
              border: `1px solid ${colors.grey[700]}`,
            }}
          >
            {members.length === 0 && (
              <Typography sx={{ p: 2, color: colors.grey[300] }}>
                No active members found.
              </Typography>
            )}
            {members.map((member) => (
              <ListItemButton
                key={member.membership_id}
                disabled={creating === member.membership_id}
                onClick={() => handleSelect(member.membership_id)}
                sx={{ borderBottom: `1px solid ${colors.grey[800]}` }}
              >
                <ListItemText
                  primary={`${member.full_name} (${member.membership_id})`}
                  secondary={`${member.email || '—'} · ${member.mobile_number || '—'}`}
                />
                {creating === member.membership_id && <CircularProgress size={20} />}
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default AssessmentMemberPicker;
